import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ChevronRight,
  Home,
  Download,
  CheckCircle,
  ArrowRight,
  BookOpen,
} from "lucide-react";
import Footer from "../components/Footer";

const ResourceLanding = ({ resources }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");

  const resource = resources?.find((r) => r.slug === slug);

  // Direct Access for Blogs check
  const isBlog = resource?.type === "Blogs";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!resource) {
    return (
      <div className="min-h-screen font-sans bg-white text-[#032d28]">
        <div className="h-20 bg-white"></div>
        <div className="max-w-7xl mx-auto px-8 py-20 text-center">
          <h2 className="text-3xl font-bold mb-4 tracking-tight">
            Resource not found
          </h2>
          <Link
            to="/resources"
            className="text-[#00a684] underline font-bold uppercase text-xs tracking-widest"
          >
            Return to Resources
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError("");

    const formData = new FormData(e.target);
    const email = formData.get("email");

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    const params = new URLSearchParams();
    params.append("email", email);
    params.append("firstName", formData.get("firstName"));
    params.append("lastName", formData.get("lastName"));
    params.append("jobTitle", formData.get("jobTitle"));
    params.append("country", formData.get("country"));
    params.append("resourceTitle", resource.title);

    try {
      const scriptURL =
        "https://script.google.com/macros/s/AKfycbxy2qrVA0ih2l6KD1KuifMrg27uThyRm4G96tl53xJkJAo9TCbT5suWScaOvi6fdAKL/exec";
      await fetch(scriptURL, { method: "POST", mode: "no-cors", body: params });
      setSubmitted(true);
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen font-sans bg-white text-[#032d28]">
      {/* bg-[#032d28]  bg-white */}
      <div className="h-20 bg-[#032d28] rounded-b-3xl"></div>{" "}
      <div className="bg-white [radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-[#eefaf7] via-white to-white pb-32">
        {/* Breadcrumbs */}
        <div className="max-w-7xl mx-auto px-8 py-8">
          <nav className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em]">
            <Link
              to="/"
              className="text-[#00a684] no-underline flex items-center gap-1"
            >
              <Home size={12} /> Home
            </Link>

            <ChevronRight size={10} className="text-gray-300" />

            <Link to="/resources" className="text-[#00a684] no-underline">
              Resources
            </Link>

            <ChevronRight size={10} className="text-gray-300" />

            <span className="text-[#00a684] truncate">{resource.title}</span>
          </nav>
        </div>

        <div className="max-w-7xl mx-auto px-8 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          {/* LEFT CONTENT */}
          <div className="lg:col-span-7 pt-4">
            <span className="text-[#00a684] font-bold text-sm mb-4 block uppercase tracking-widest border-b border-[#00a684]/20 w-fit pb-1">
              {resource.type}
            </span>
            <h1 className="text-4xl md:text-[50px] font-bold leading-[1.1] mb-10 tracking-tight text-[#032d28]">
              {resource.details?.heroTitle}
            </h1>

            <div className="space-y-8 text-[18px] leading-relaxed text-[#4a5568]">
              <p className="font-semibold text-[#032d28] text-[21px]">
                {resource.details?.introText}
              </p>
              <p>{resource.details?.mainParagraph}</p>

              <div className="pt-6">
                <p className="font-extrabold text-[#032d28] mb-6 uppercase text-xs tracking-[0.2em] opacity-80">
                  In Synthera's view, this reflects:
                </p>
                <ul className="space-y-5">
                  {resource.details?.bullets?.map((item, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <span className="text-[#00a684] text-xl leading-none mt-1">
                        •
                      </span>
                      <span className="text-[16px] text-[#4a5568] font-medium">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: FORM OR DIRECT ACCESS */}
          <div className="lg:col-span-5 relative mt-4">
            <div className="lg:sticky lg:top-28 bg-[#f1f9f7] p-10 md:p-14 rounded-[50px] shadow-[0_30px_60px_-15px_rgba(0,166,132,0.12)] border border-[#e2f2ef] transition-all duration-500">
              {isBlog ? (
                /* Conditional Rendering: Direct Access for Blogs */
                <div className="text-center py-6 animate-in fade-in zoom-in duration-500">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-[#e2f2ef]">
                    <BookOpen className="text-[#00a684] w-10 h-10" />
                  </div>
                  <h2 className="text-2xl font-bold mb-4 text-[#006b54] tracking-tight">
                    Ready to Read
                  </h2>
                  <p className="text-gray-500 mb-10 text-[15px] font-medium">
                    This article is available for immediate access. No
                    registration required.
                  </p>
                  <button
                    onClick={() => navigate(resource.blogUrl)}
                    className="flex items-center justify-center gap-3 w-full bg-[#032d28] text-white font-bold py-6 rounded-full hover:bg-black transition-all shadow-xl text-[14px] uppercase tracking-widest"
                  >
                    Open Full Article <ArrowRight size={18} />
                  </button>
                  <p className="text-[10px] text-gray-400 mt-10 font-bold uppercase tracking-[0.2em] opacity-60">
                    Synthera Insights
                  </p>
                </div>
              ) : /* SHOW FORM ONLY FOR WHITEPAPERS/REPORTS */
              !submitted ? (
                <>
                  <h2 className="text-2xl md:text-3xl font-bold mb-10 text-[#006b54] tracking-tight">
                    Download the {resource.type}
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-8">
                    <div className="relative border-b-2 border-[#00a684]/20 pb-1 focus-within:border-[#00a684] transition-all">
                      <label className="block text-[11px] text-[#006b54] font-black uppercase tracking-wider mb-1">
                        * Work Email
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="you@company.com"
                        className="w-full bg-transparent py-1 outline-none text-[#2d3748] font-bold text-lg placeholder:text-gray-300 placeholder:font-normal"
                      />
                      {emailError && (
                        <p className="absolute -bottom-6 left-0 text-red-500 text-[10px] font-bold uppercase">
                          {emailError}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-10">
                      <div className="relative border-b-2 border-[#00a684]/20 pb-1 focus-within:border-[#00a684] transition-all">
                        <label className="block text-[11px] text-[#006b54] font-black uppercase tracking-wider mb-1">
                          * First Name
                        </label>
                        <input
                          name="firstName"
                          type="text"
                          required
                          className="w-full bg-transparent py-1 outline-none text-[#2d3748] font-bold"
                        />
                      </div>
                      <div className="relative border-b-2 border-[#00a684]/20 pb-1 focus-within:border-[#00a684] transition-all">
                        <label className="block text-[11px] text-[#006b54] font-black uppercase tracking-wider mb-1">
                          * Last Name
                        </label>
                        <input
                          name="lastName"
                          type="text"
                          required
                          className="w-full bg-transparent py-1 outline-none text-[#2d3748] font-bold"
                        />
                      </div>
                    </div>
                    <div className="relative border-b-2 border-[#00a684]/20 pb-1 focus-within:border-[#00a684] transition-all">
                      <label className="block text-[11px] text-[#006b54] font-black uppercase tracking-wider mb-1">
                        * Job Title
                      </label>
                      <input
                        name="jobTitle"
                        type="text"
                        required
                        className="w-full bg-transparent py-1 outline-none text-[#2d3748] font-bold"
                      />
                    </div>
                    <div className="relative border-b-2 border-[#00a684]/20 pb-1 focus-within:border-[#00a684] transition-all">
                      <label className="block text-[11px] text-[#006b54] font-black uppercase tracking-wider mb-1">
                        * Country
                      </label>
                      <select
                        name="country"
                        required
                        className="w-full bg-transparent py-2 outline-none appearance-none text-gray-600 font-bold cursor-pointer"
                      >
                        <option value="">Select Country</option>
                        {[
                          "Australia",
                          "India",
                          "New Zealand",
                          "Qatar",
                          "Sri Lanka",
                          "Saudi Arabia",
                          "Singapore",
                          "UAE",
                          "United Kingdom",
                          "United States",
                        ].map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </select>
                      <ChevronRight
                        size={16}
                        className="absolute right-0 bottom-4 rotate-90 text-[#00a684] pointer-events-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`w-full ${loading ? "bg-gray-300" : "bg-[#fdb913] hover:bg-black hover:text-white"} text-[#032d28] font-black py-6 rounded-full transition-all duration-300 mt-8 shadow-md text-sm uppercase tracking-[0.15em] transform active:scale-95`}
                    >
                      {loading ? "Processing..." : "Get it Now"}
                    </button>
                  </form>
                </>
              ) : (
                <div className="animate-in fade-in zoom-in duration-700 text-center py-10">
                  <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner border border-gray-50">
                    <CheckCircle className="text-[#00a684] w-14 h-14" />
                  </div>
                  <h3 className="text-3xl font-bold text-[#006b54] mb-4 tracking-tight">
                    Success
                  </h3>
                  <p className="text-gray-500 mb-10 text-[15px] font-medium">
                    Your access has been granted.
                  </p>
                  <a
                    href={resource.fileUrl}
                    download
                    className="flex items-center justify-center gap-3 w-full bg-[#00a684] text-white font-bold py-6 rounded-full hover:bg-[#008c6f] transition-all shadow-lg text-[14px] uppercase tracking-widest"
                  >
                    <Download size={20} /> Download PDF
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResourceLanding;
